import { z } from "zod";

export const STAGING_ADMIN_USER_ID = "user_2XGaD3CjLziJsbsGAFHhpj7Az07";
export const PRODUCTION_ADMIN_USER_ID = "user_2MUTyK3k1ytXsW42PKKNqu4Dt80";

const appEnvironmentSchema = z.enum(["dev", "stg", "prd"]);
const adminUserIdsByEnvironment: {
  [environment: string]: string;
} = {
  dev: STAGING_ADMIN_USER_ID,
  stg: STAGING_ADMIN_USER_ID,
  prd: PRODUCTION_ADMIN_USER_ID,
};

/**
 * Return the support admin for the deployment environment. The environment is
 * intentionally explicit so a staging identity cannot authorize in production.
 */
export function getAdminUserId(environment: string): string {
  const parsedEnvironment = appEnvironmentSchema.safeParse(environment);
  if (!parsedEnvironment.success) {
    throw new Error(`Unsupported deployment environment: ${environment}`);
  }
  return adminUserIdsByEnvironment[parsedEnvironment.data];
}

export function isAdminUserId(actorId: string | null | undefined, environment: string): boolean {
  const parsedEnvironment = appEnvironmentSchema.safeParse(environment);
  if (actorId === null || actorId === undefined || !parsedEnvironment.success) {
    return false;
  }
  return actorId === adminUserIdsByEnvironment[parsedEnvironment.data];
}

export const IMPERSONATION_READ_ONLY_ERROR =
  "Mutations are disabled while impersonating; impersonation is read-only. Exit impersonation before making changes.";

const impersonationTargetSchema = z.string().trim().min(1);
const activeAdminImpersonationSchema = z.object({
  targetId: impersonationTargetSchema,
  expiresAt: z.coerce.date().transform((date) => date.toISOString()),
});

export interface ActiveAdminImpersonation {
  targetId: string;
  expiresAt: string;
}

export interface EffectiveIdentity {
  actorId: string;
  effectiveId: string;
  impersonating: string | null;
  impersonationExpiresAt: string | null;
  isImpersonating: boolean;
}

export interface IdentityInput {
  actorId: string | null | undefined;
  impersonating: unknown;
  /** The deployment-specific support admin. Defaults to staging for pure callers/tests. */
  adminUserId?: string;
}

/**
 * Resolve the data-access identity without ever allowing an untrusted actor to
 * choose a target. A target is accepted only for the configured support admin.
 */
export function resolveEffectiveIdentity({
  actorId,
  impersonating,
  adminUserId = STAGING_ADMIN_USER_ID,
}: IdentityInput): EffectiveIdentity | null {
  if (!actorId) {
    return null;
  }

  const target = activeAdminImpersonationSchema.safeParse(impersonating);
  const isImpersonating = actorId === adminUserId && target.success;

  return {
    actorId,
    effectiveId: isImpersonating ? target.data.targetId : actorId,
    impersonating: isImpersonating ? target.data.targetId : null,
    impersonationExpiresAt: isImpersonating ? target.data.expiresAt : null,
    isImpersonating,
  };
}

export function assertMutationAllowed(identity: EffectiveIdentity): void {
  if (identity.effectiveId !== identity.actorId) {
    throw new Error(IMPERSONATION_READ_ONLY_ERROR);
  }
}

export function assertClientMutationAllowed(isImpersonating: boolean): void {
  if (isImpersonating) {
    throw new Error(IMPERSONATION_READ_ONLY_ERROR);
  }
}
